import React from "react";
import { Avatar, Card, Tooltip } from "antd";
import styled from "styled-components";
import { Company, PLSeason } from "../../types";
import { logoResolver } from "../../utils";

const SBoxesWrapper = styled.div`
  display: flex;
  gap: 16px;
`;

const SBox = styled(Card)`
  flex: 1;
`;

const SLogos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const SClubsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, max-content);
  gap: 12px;
`;

const SGreyedAvatar = styled(Avatar)`
  filter: grayscale(100%);
  opacity: 0.5;
`;

const ClubLogo = ({
  company,
  greyedOut,
}: {
  company: Company;
  greyedOut?: boolean;
}) => (
  <Tooltip title={company.name}>
    {greyedOut ? (
      <SGreyedAvatar
        shape="square"
        size="large"
        src={logoResolver(company.logo)}
        alt={company.name}
      />
    ) : (
      <Avatar
        shape="square"
        size="large"
        src={logoResolver(company.logo)}
        alt={company.name}
      />
    )}
  </Tooltip>
);

type SeasonDetailsProps = {
  season: PLSeason;
  nextSeason?: PLSeason;
  companies: Company[];
};

const SeasonDetails = ({
  season,
  nextSeason,
  companies,
}: SeasonDetailsProps) => {
  const currentClubs = companies.filter(
    (company) =>
      company.plSeasons?.some((plSeason) => plSeason.id === season.id),
  );

  const nextSeasonClubs = nextSeason
    ? companies.filter(
        (company) =>
          company.plSeasons?.some((plSeason) => plSeason.id === nextSeason.id),
      )
    : [];

  // when there's no next season yet (i.e. this is the current season), we simply
  // don't know who's getting promoted/relegated - so both boxes stay empty
  const promotedClubs = nextSeason
    ? nextSeasonClubs.filter(
        (company) => !currentClubs.some((c) => c.id === company.id),
      )
    : [];

  const relegatedClubs = nextSeason
    ? currentClubs.filter(
        (company) => !nextSeasonClubs.some((c) => c.id === company.id),
      )
    : [];

  return (
    <SBoxesWrapper>
      <SBox title={`Clubs in ${season.label}`} size="small">
        <SClubsGrid>
          {currentClubs.map((company) => (
            <ClubLogo key={company.id} company={company} />
          ))}
        </SClubsGrid>
      </SBox>
      <SBox title="Promoted Teams" size="small">
        <SLogos>
          {promotedClubs.map((company) => (
            <ClubLogo key={company.id} company={company} />
          ))}
        </SLogos>
      </SBox>
      <SBox title="Relegated Teams" size="small">
        <SLogos>
          {relegatedClubs.map((company) => (
            <ClubLogo key={company.id} company={company} greyedOut />
          ))}
        </SLogos>
      </SBox>
    </SBoxesWrapper>
  );
};

export default SeasonDetails;
